"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import { track } from "./_analytics/track";

type FormStatus = "idle" | "sending" | "sent" | "email" | "error";

/** De velden die de server kent. Alles daarbuiten weigert hij. */
const VELDEN = [
  "name",
  "email",
  "phone",
  "location",
  "phase",
  "budget",
  "timeline",
  "message",
  "website",
] as const;

type Challenge = { token: string; minFillMs: number };

/**
 * De botcontrole van de server. De pagina haalt hem op zodra hij openstaat, dus
 * bij het versturen is de minimale invultijd allang verstreken. Alleen als dat
 * ophalen mislukte doen we het alsnog bij het versturen, en wachten we die tijd
 * bewust uit — vervelend voor die ene bezoeker, maar wel eerlijk.
 */
async function haalChallenge(): Promise<Challenge | null> {
  try {
    const response = await fetch("/api/contact", { headers: { Accept: "application/json" } });
    if (!response.ok) return null;
    const data = (await response.json()) as { challenge?: unknown; minFillMs?: unknown };
    if (typeof data.challenge !== "string") return null;
    return {
      token: data.challenge,
      minFillMs: typeof data.minFillMs === "number" ? data.minFillMs : 3000,
    };
  } catch {
    return null;
  }
}

function emailLink(form: FormData) {
  const subject = "Mijn plan voor eVilla";
  const body = [
    `Naam: ${form.get("name") ?? ""}`,
    `E-mail: ${form.get("email") ?? ""}`,
    `Telefoon: ${form.get("phone") ?? ""}`,
    `Plaats of kavel: ${form.get("location") ?? ""}`,
    `Planfase: ${form.get("phase") ?? ""}`,
    `Budget: ${form.get("budget") ?? ""}`,
    `Planning: ${form.get("timeline") ?? ""}`,
    "",
    "Mijn plan:",
    String(form.get("message") ?? ""),
  ].join("\n");

  return `mailto:info@evilla.nl?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * De uitkomst van de projectrouter, als de bezoeker daar langs kwam. Alleen de
 * route en de pakketindicatie; de antwoorden zelf blijven waar ze staan.
 */
function kwalificatieMetadata(): { selected_route?: string; pakketindicatie?: string } {
  try {
    const rauw = window.sessionStorage.getItem("evilla:leadkwalificatie:v1");
    if (!rauw) return {};
    const { route, pakketindicatie } = JSON.parse(rauw) as {
      route?: unknown;
      pakketindicatie?: unknown;
    };
    return {
      ...(typeof route === "string" ? { selected_route: route } : {}),
      ...(typeof pakketindicatie === "string" ? { pakketindicatie } : {}),
    };
  } catch {
    return {};
  }
}

/**
 * `defaultPhase` vult alleen het bestaande planfaseveld voor. De acht sleutels
 * die naar `/api/contact` gaan blijven ongewijzigd; de projectroute zelf hoort
 * pas in de kwalificatie-envelop van U5.
 */
export function ContactForm({ defaultPhase = "" }: { defaultPhase?: string }) {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [fallbackLink, setFallbackLink] = useState("mailto:info@evilla.nl");
  const [foutmelding, setFoutmelding] = useState("");

  const challenge = useRef<Challenge | null>(null);

  /**
   * Eén willekeurige aanvraagcode per ingevuld formulier. Bij herhalen gaat
   * dezelfde code mee, zodat een tweede poging na een storing niet als tweede
   * aanvraag bij de ontvanger belandt. Pas na een geslaagde verzending begint
   * een volgende invulbeurt met een nieuwe code.
   */
  const aanvraagcode = useRef<string | null>(null);

  function huidigeAanvraagcode() {
    if (aanvraagcode.current == null) aanvraagcode.current = crypto.randomUUID();
    return aanvraagcode.current;
  }

  useEffect(() => {
    let levend = true;
    haalChallenge().then((verse) => {
      if (levend) challenge.current = verse;
    });
    return () => {
      levend = false;
    };
  }, []);

  async function submitContact(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const mailto = emailLink(form);
    setFallbackLink(mailto);
    setFoutmelding("");
    setStatus("sending");

    // Alleen de planfase en de kwalificatie uit de projectrouter gaan mee als
    // metadata. Naam, e-mail, telefoon, locatie en het bericht zelf horen niet
    // in analytics.
    const phase = String(form.get("phase") ?? "onbekend");
    const meting = { component: "contact-form", phase, ...kwalificatieMetadata() };

    if (!challenge.current) {
      const laat = await haalChallenge();
      if (!laat) {
        track("consultation_requested", { ...meting, delivery: "mailto" });
        setStatus("email");
        window.location.href = mailto;
        return;
      }
      await new Promise((klaar) => setTimeout(klaar, laat.minFillMs));
      challenge.current = laat;
    }

    const payload: Record<string, string> = {
      challenge: challenge.current.token,
      submissionCode: huidigeAanvraagcode(),
    };
    for (const veld of VELDEN) {
      const waarde = form.get(veld);
      if (typeof waarde === "string" && waarde !== "") payload[veld] = waarde;
    }

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        track("consultation_requested", { ...meting, delivery: "api" });
        formElement.reset();
        // Een volgende invulbeurt is een nieuwe aanvraag, dus een nieuwe code
        // en een verse botcontrole.
        aanvraagcode.current = null;
        challenge.current = await haalChallenge();
        setStatus("sent");
        return;
      }

      if (response.status === 503) {
        track("consultation_requested", { ...meting, delivery: "mailto" });
        setStatus("email");
        window.location.href = mailto;
        return;
      }

      // De server weigerde de aanvraag zelf: te snel, te vaak, te groot of iets
      // dat niet klopt. Zijn uitleg is bruikbaarder dan onze algemene fout.
      const uitleg = await response
        .json()
        .then((data: { message?: unknown }) =>
          typeof data.message === "string" ? data.message : "",
        )
        .catch(() => "");

      // Een verlopen of opgebruikte botcontrole is te herstellen; haal een
      // verse op zodat de volgende poging wel doorkomt.
      if (response.status === 400) challenge.current = await haalChallenge();

      setFoutmelding(uitleg);
      setStatus("error");
    } catch {
      setStatus("error");
    }
  }

  return (
    <form className="contact-form" onSubmit={submitContact}>
      <div className="field-row">
        <label>
          Naam
          <input name="name" autoComplete="name" maxLength={120} required />
        </label>
        <label>
          E-mailadres
          <input
            name="email"
            type="email"
            autoComplete="email"
            maxLength={254}
            required
          />
        </label>
      </div>

      <div className="field-row">
        <label>
          Telefoonnummer
          <input name="phone" type="tel" autoComplete="tel" maxLength={40} />
        </label>
        <label>
          Plaats of kavel
          <input name="location" autoComplete="address-level2" maxLength={160} />
        </label>
      </div>

      <div className="field-row">
        <label>
          Hoe ver ben je?
          <select name="phase" defaultValue={defaultPhase}>
            <option value="" disabled>
              Kies wat het meest lijkt
            </option>
            <option value="idee">Ik heb een idee</option>
            <option value="schets">Ik heb een schets</option>
            <option value="ontwerp">Ik heb een ontwerp</option>
            <option value="vergunning">Mijn vergunning loopt of is rond</option>
          </select>
        </label>
        <label>
          Wat is je budget ongeveer?
          <input name="budget" maxLength={120} placeholder="Bijvoorbeeld 600.000 euro, zonder de kavel" />
        </label>
      </div>

      <label>
        Wanneer wil je erin?
        <input
          name="timeline"
          maxLength={160}
          placeholder="Bijvoorbeeld zomer 2027"
        />
      </label>

      <label>
        Vertel over je plan
        <textarea
          name="message"
          rows={5}
          maxLength={4000}
          required
          placeholder="Wat wil je bouwen, waar komt het en wat wil je nu het liefst weten?"
        />
      </label>

      <label className="form-honeypot" aria-hidden="true">
        Website
        <input name="website" tabIndex={-1} autoComplete="off" />
      </label>

      <button
        className="button button-primary form-button"
        type="submit"
        disabled={status === "sending"}
      >
        {status === "sending" ? "Even geduld, we versturen je plan" : "Leg je plan voor"}
      </button>

      <div className="form-status" aria-live="polite">
        {status === "sent" && <p>Je plan is binnen. Binnen een week hoor je van ons of het kan en wat het ongeveer kost.</p>}
        {status === "email" && (
          <p>
            Je e-mailprogramma opent nu. Gebeurt dat niet, {" "}
            <a href={fallbackLink}>open het bericht dan zelf.</a>
          </p>
        )}
        {status === "error" && (
          <p>
            {foutmelding || "Versturen lukt nu even niet."}{" "}
            <a href={fallbackLink}>Stuur je plan anders per e-mail naar info@evilla.nl.</a>
          </p>
        )}
        {status === "idle" && (
          <p>Hoe meer je nu vertelt, hoe scherper het antwoord dat je terugkrijgt.</p>
        )}
      </div>
    </form>
  );
}
